<script setup>
import { onMounted, ref, toRaw, watch, computed } from "vue";
import LocalStorage from '@/controller/LocalStorage.js';

const props = defineProps({
  total: Number,
  pagNumber: Number,
  limit: Number
});

const emit = defineEmits(["loadPage","updateLimit"]);
const pagLimit = ref(props.limit ?? 12);
const numPages = ref(Math.ceil(parseInt(props.total) / pagLimit.value)); // by now
const userColor = ref(LocalStorage.getColor());
const defaultPages = [1, 2, 3, 4, 5];
const pagesShow = numPages.value > 5 ? ref(defaultPages) : ref(defaultPages.slice(0, numPages.value));
const paginationLimit = [ 4, 8, 20 ];
const totalPagesArray = generateArray(numPages.value);

function changePaginationLimit(){
  numPages.value = Math.ceil(parseInt(props.total) / pagLimit.value);
  recalcPagesShow(props.pagNumber);
  emit('updateLimit', pagLimit.value);
  emit('loadPage', 1);
}


function goToPage(index) {
  recalcPagesShow(index);
  emit('loadPage', index);
}

function recalcPagesShow(index){
  let totalPages = toRaw(numPages.value);
  if(totalPages <= 5){
    pagesShow.value = defaultPages.slice(0, totalPages);
  }else if(totalPages > 5 && index < 5){
    pagesShow.value = defaultPages;
  }else if(totalPages > 5 && index >= 5 && index <= totalPages - 2){
    pagesShow.value = totalPagesArray.slice(index - 3, index + 2);
  }else if(totalPages == index){
    pagesShow.value = totalPagesArray.slice(totalPages - 5, totalPages);
  }else if(totalPages > 5 && index == 1){
    pagesShow.value = defaultPages;
  }
}

function prevPage() {
  if (props.pagNumber > 1) {
    emit("loadPage", props.pagNumber - 1);
  }
}

function nextPage() {
  if (props.pagNumber < numPages.value) {
    emit("loadPage", props.pagNumber + 1);
  }
}

const dontShow = computed(()=> {
  if(numPages.value === 1){
    return{
      display: 'none'
    }
  }else{
    return{
      display: 'flex'
    }
  }
}); 
 


function goToFirstPage() {
  emit('loadPage', 1);
}

function goToLastPage() {
  emit('loadPage', numPages.value);
}

function generateArray(n) {
  let array = [];
  for (let i = 1; i <= n; i++) {
    array.push(i);
  }
  return array;
}

onMounted(() => {
  recalcPagesShow(props.pagNumber);
});
watch(() => props.pagNumber, () => {
  recalcPagesShow(props.pagNumber);
});

</script>

<template>
  <div class="custom-table-pagination-wrapper" :style="dontShow">
    <div class="pagination-limit">
      <!-- <select v-model="pagLimit" @change="changePaginationLimit()" id="paginationLimit">
        <option
          v-for="(pag, i) in paginationLimit"
          :key="`locale-${i}`"
          :value="pag"
          :selected="pag === pagLimit"
        >
          {{ pag }}
        </option>
      </select> -->
    </div>
    <div class="custom-table-paginator-content" >
      <div
        v-if="numPages > 5"
        class="paginator-number btn-paginator"
        :class="[props.pagNumber === 1 ? 'disabled' : '']"
        @click="goToFirstPage"
      >
        <span class="btn-chevron">&laquo;</span>
      </div>
      <div
        v-if="numPages > 1"
        class="paginator-number btn-paginator"
        :class="[props.pagNumber === 1 ? 'disabled' : '']"
        @click="prevPage"
      >
        <span class="btn-chevron">&lsaquo;</span>
      </div>
      <div
        class="paginator-number btn-paginator"
        v-for="index in pagesShow"
        :key="index"
        :class="index == props.pagNumber ? 'selected' : ''"
        @click="goToPage(index)"
      >
        <span>{{ index }}</span>
      </div>
      <div
        v-if="numPages > 1"
        class="paginator-number btn-paginator"
        :class="[props.pagNumber === numPages ? 'disabled' : '']"
        @click="nextPage"
      >
        <span class="btn-chevron">&rsaquo;</span>
      </div>
      <div
        v-if="numPages > 5"
        class="paginator-number btn-paginator"
        :class="[props.pagNumber === numPages ? 'disabled' : '']"
        @click="goToLastPage"
      >
        <span class="btn-chevron">&raquo;</span>
      </div>
    </div>
    <div class="pagination-current">
      {{ props.pagNumber + ' / ' + numPages }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'src/styles/colors.scss';
.custom-table-pagination-wrapper {
  font-family: 'CircularStd-Book';
  display: grid;
  grid-template-columns: 200px 1fr 200px;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;    
  height: 50px;
  padding-top: 4px;
  font-size: 13px;
  background-color: transparent;
  .pagination-limit select{
    height: 2rem;
    width: 2rem;
    border-radius: 5px;
    padding: 2px 8px;
    -moz-appearance:none;
    -webkit-appearance:none;
    appearance:none;
    &:focus{
      outline: none;
    }
  }
  .pagination-limit{ justify-self: start;}
  .custom-table-paginator-content{justify-self: center;}
  .pagination-current{justify-self: end;}
  .pagination-limit,
  .custom-table-paginator-content,
  .pagination-current{
    flex-grow: 0;
  }
  .custom-table-paginator-content {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #dcdcdc;
    border-radius: 6px;
    .paginator-number {
      display: flex;
      justify-content: center;
      align-items: center;
      &.selected {
        color: white;
        background: v-bind(userColor);
        border-color: v-bind(userColor);
      }
    }
    .paginator-number:not([class*="disabled"]):hover{
      background: v-bind(userColor);
      border-color: v-bind(userColor);
      color: white;
    }
    .btn-paginator {
      color: #939eae;
      cursor: pointer;
      background-color: transparent;
      border: 0 none;
      height: 2rem;
      width: 2rem;
      margin: 2px;
      transition: box-shadow 0.2s;
      border-radius: 50%;
      &.disabled {
        cursor: no-drop;
      }
      span.btn-chevron{
        position: absolute;
        top: -1px;
        font-weight: 400;
        font-size: 20px;
      }
    }
  }
}
@media (max-width:360px){
  .custom-table-paginator-content {
    position: absolute;
    max-width: 80dvw !important;
    margin: auto;
}
  .custom-table-pagination-wrapper{
    justify-content: center;
    margin: 0px !important;
    padding: 0px !important;
  }
  
  .pagination-current{
    display: none;
  }
}
@media ((min-width:361px) and (max-width:431px)){
  .custom-table-pagination-wrapper{
    display: flex;
    justify-content: center;
    max-width: 90%;
    margin: auto;
  }
  .pagination-current{
    display: none;
  }
}

@media ((min-width:431px) and (max-width: 576px)) {
  .custom-table-pagination-wrapper{
    display: flex;
    justify-content: center;
    max-width: 90%;
    margin: auto;
  }
  .pagination-current{
    display: none;
  }
}

//tablets verticales
@media (min-width: 577px) and (max-width: 1024px) and (orientation: portrait){

  .custom-table-pagination-wrapper{
    display: flex;
    justify-content: center;
    max-width: 90%;
    margin: auto;
  }
  .pagination-current{
    display: none;
  }
}


</style>
